/**
 * @architecture-header 1
 * @layer module
 * @domain activities
 * @responsibility Compose independent Desktop paper and Mobile stacked ACT-F12 question editors.
 * @state-owner local controlled drafts and selected image; TanStack Query owns canonical rows
 * @allowed-imports React; same-module mutations and models; shared viewport and notification hooks
 * @forbidden direct API client; raw asset identifiers; demo entity writes
 * @public-api ActivityEditor
 * @side-effects authenticated image upload/question mutations and accessible status notifications
 * @tests ActivityItemComposer.test.tsx, ../api/activities.mutations.test.ts and ../../../../../../apps/web/e2e/activity-item-composer.spec.ts
 */

import { type DragEvent, useEffect, useRef, useState } from 'react';
import { useIsMobile, useNotify } from '../../../shared/model/use-workspace';
import {
  useCreateActivityItem,
  useDeleteActivityItem,
  useDuplicateActivityItem,
  useGenerateSimilarActivityItem,
  useImportSingleQuestion,
  useReorderActivityItems,
  useUpdateActivityItem,
  useUploadSingleQuestionImage,
} from '../api/activities.mutations';
import type {
  ActivityEditorItem,
  ActivityItemCreationAttempt,
  ActivityItemDraft,
} from '../model/types';

const acceptedImageTypes = 'image/png,image/jpeg,image/webp';
const maximumImageBytes = 10 * 1024 * 1024;

interface ComposerViewProps {
  activityRevision: number;
  attempts: ActivityItemCreationAttempt[];
  drafts: Record<string, ActivityItemDraft>;
  items: ActivityEditorItem[];
  pending: boolean;
  selectedImage: File | null;
  error?: string;
  onAdd(type: ActivityItemDraft['itemType']): void;
  onDelete(item: ActivityEditorItem): void;
  onDraft(itemId: string, draft: ActivityItemDraft): void;
  onDuplicate(item: ActivityEditorItem): void;
  onGenerate(item: ActivityEditorItem): void;
  onImageSelected(file: File | null): void;
  onImport(): void;
  onMove(item: ActivityEditorItem, direction: -1 | 1): void;
  onReorder(sourceItemId: string, targetItemId: string): void;
  onSave(item: ActivityEditorItem): void;
}

const typeLabels: Record<ActivityItemDraft['itemType'], string> = {
  choice: '選擇題',
  true_false: '是非題',
  short_answer: '簡答題',
  drawing: '繪圖題',
};

function nextKey(prefix: string): string {
  return `${prefix}-${crypto.randomUUID()}`;
}

function draftFromItem(item: ActivityEditorItem): ActivityItemDraft {
  const itemType = ['choice', 'true_false', 'short_answer', 'drawing'].includes(item.itemType)
    ? (item.itemType as ActivityItemDraft['itemType'])
    : 'short_answer';
  return {
    itemType,
    ...(itemType === 'choice'
      ? { selectionMode: item.selectionMode === 'multiple' ? 'multiple' : 'single' }
      : {}),
    questionContent: item.questionContent,
    options: itemType === 'choice' ? item.options : [],
    answerKey: item.answerKey,
    maxPoints: item.maxPoints,
    requiresShowWork: item.requiresShowWork,
  };
}

function blankDraft(itemType: ActivityItemDraft['itemType']): ActivityItemDraft {
  return {
    itemType,
    ...(itemType === 'choice' ? { selectionMode: 'single' as const } : {}),
    questionContent: { text: '新題目' },
    options:
      itemType === 'choice'
        ? [
            { id: crypto.randomUUID(), text: '選項 A' },
            { id: crypto.randomUUID(), text: '選項 B' },
          ]
        : [],
    maxPoints: 1,
    requiresShowWork: false,
  };
}

function changedItemType(
  draft: ActivityItemDraft,
  itemType: ActivityItemDraft['itemType'],
): ActivityItemDraft {
  return {
    ...draft,
    itemType,
    ...(itemType === 'choice'
      ? {
          selectionMode: draft.selectionMode ?? 'single',
          options: draft.options.length >= 2 ? draft.options : blankDraft('choice').options,
        }
      : { selectionMode: undefined, options: [] }),
  };
}

function AttemptMessages({ attempts, error }: Pick<ComposerViewProps, 'attempts' | 'error'>) {
  return (
    <>
      {error ? (
        <p className="activity-item-error" role="alert">
          {error}
        </p>
      ) : null}
      {attempts.map((attempt) => (
        <p className={`activity-item-attempt ${attempt.state}`} key={attempt.id} role="status">
          {attempt.kind === 'image_import' ? '圖片匯入' : '產生相似題'}：{attempt.state}
          {attempt.errorCode ? ` (${attempt.errorCode})` : ''}
        </p>
      ))}
    </>
  );
}

function DesktopQuestionFields({
  draft,
  index,
  item,
  onDraft,
}: {
  draft: ActivityItemDraft;
  index: number;
  item: ActivityEditorItem;
  onDraft(itemId: string, draft: ActivityItemDraft): void;
}) {
  const prompt = String(draft.questionContent.text ?? draft.questionContent.prompt ?? '');
  return (
    <div className="activity-item-desktop-fields">
      <label>
        題幹
        <textarea
          aria-label={`第 ${index + 1} 題題幹`}
          value={prompt}
          onChange={(event) =>
            onDraft(item.id, {
              ...draft,
              questionContent: { ...draft.questionContent, text: event.target.value },
            })
          }
        />
      </label>
      {draft.itemType === 'choice' ? (
        <label>
          選項（每行一個）
          <textarea
            aria-label={`第 ${index + 1} 題選項`}
            value={draft.options.map((option) => String(option.text ?? '')).join('\n')}
            onChange={(event) =>
              onDraft(item.id, {
                ...draft,
                options: event.target.value.split('\n').map((text, optionIndex) => ({
                  id: String(draft.options[optionIndex]?.id ?? crypto.randomUUID()),
                  text,
                })),
              })
            }
          />
        </label>
      ) : null}
      <div className="activity-item-fields">
        <label>
          答案
          <input
            aria-label={`第 ${index + 1} 題答案`}
            value={String(draft.answerKey?.answer ?? '')}
            onChange={(event) =>
              onDraft(item.id, {
                ...draft,
                answerKey: { ...draft.answerKey, answer: event.target.value },
              })
            }
          />
        </label>
        <label>
          配分
          <input
            aria-label={`第 ${index + 1} 題配分`}
            min="0.01"
            step="0.5"
            type="number"
            value={draft.maxPoints}
            onChange={(event) =>
              onDraft(item.id, {
                ...draft,
                maxPoints: Math.max(0.01, Number(event.target.value) || 1),
              })
            }
          />
        </label>
        <label className="activity-item-checkbox">
          <input
            checked={draft.requiresShowWork}
            type="checkbox"
            onChange={(event) =>
              onDraft(item.id, { ...draft, requiresShowWork: event.target.checked })
            }
          />
          需列式
        </label>
      </div>
    </div>
  );
}

export function DesktopActivityItemComposerView(props: ComposerViewProps) {
  const [draggedItemId, setDraggedItemId] = useState<string | null>(null);
  return (
    <section
      aria-label="桌面版卷面題目編輯器"
      className="activity-item-composer-paper"
      data-testid="desktop-item-editor"
    >
      <header className="activity-item-composer-toolbar desktop-composer-toolbar">
        <div>
          <b>題目編輯</b>
          <span>{`草稿版本 ${props.activityRevision}`}</span>
        </div>
        <fieldset className="activity-item-add-actions">
          <legend className="sr-only">新增題型</legend>
          {(Object.keys(typeLabels) as ActivityItemDraft['itemType'][]).map((type) => (
            <button
              disabled={props.pending}
              key={type}
              type="button"
              onClick={() => props.onAdd(type)}
            >
              {`新增${typeLabels[type]}`}
            </button>
          ))}
        </fieldset>
      </header>
      <div className="activity-item-import desktop-image-import">
        <label className="activity-image-picker">
          選擇單題圖片
          <input
            accept={acceptedImageTypes}
            aria-label="選擇單題圖片"
            key={props.selectedImage?.name ?? 'empty'}
            type="file"
            onChange={(event) => props.onImageSelected(event.target.files?.[0] ?? null)}
          />
        </label>
        <span>{props.selectedImage?.name ?? '尚未選擇圖片'}</span>
        <button
          disabled={props.pending || !props.selectedImage}
          type="button"
          onClick={props.onImport}
        >
          上傳並匯入單題
        </button>
      </div>
      <AttemptMessages attempts={props.attempts} error={props.error} />
      {props.items.length === 0 ? (
        <div className="activity-item-composer-empty" role="status">
          <b>這是一份空白草稿</b>
          <p>選擇題型開始建立第一題。</p>
        </div>
      ) : (
        <div className="activity-item-composer-list desktop-question-sheets">
          {props.items.map((item, index) => {
            const draft = props.drafts[item.id] ?? draftFromItem(item);
            return (
              <article
                className="activity-item-card desktop-question-sheet"
                data-activity-item-id={item.id}
                draggable
                key={item.id}
                onDragStart={() => setDraggedItemId(item.id)}
                onDragOver={(event: DragEvent<HTMLElement>) => event.preventDefault()}
                onDrop={() => {
                  if (draggedItemId && draggedItemId !== item.id)
                    props.onReorder(draggedItemId, item.id);
                  setDraggedItemId(null);
                }}
              >
                <header>
                  <span className="activity-item-drag-handle" aria-hidden="true">
                    ⋮⋮
                  </span>
                  <span className="activity-item-number">{index + 1}</span>
                  <select
                    aria-label={`第 ${index + 1} 題題型`}
                    value={draft.itemType}
                    onChange={(event) =>
                      props.onDraft(
                        item.id,
                        changedItemType(draft, event.target.value as ActivityItemDraft['itemType']),
                      )
                    }
                  >
                    {(Object.keys(typeLabels) as ActivityItemDraft['itemType'][]).map((type) => (
                      <option key={type} value={type}>
                        {typeLabels[type]}
                      </option>
                    ))}
                  </select>
                  <span className="activity-item-revision">{`v${item.revision}`}</span>
                </header>
                <DesktopQuestionFields
                  draft={draft}
                  index={index}
                  item={item}
                  onDraft={props.onDraft}
                />
                <footer className="desktop-question-actions">
                  <button
                    disabled={props.pending || index === 0}
                    type="button"
                    onClick={() => props.onMove(item, -1)}
                  >
                    上移
                  </button>
                  <button
                    disabled={props.pending || index === props.items.length - 1}
                    type="button"
                    onClick={() => props.onMove(item, 1)}
                  >
                    下移
                  </button>
                  <button
                    disabled={props.pending}
                    type="button"
                    onClick={() => props.onDuplicate(item)}
                  >
                    複製
                  </button>
                  <button
                    disabled={props.pending}
                    type="button"
                    onClick={() => props.onGenerate(item)}
                  >
                    產生相似題
                  </button>
                  <button
                    disabled={props.pending}
                    type="button"
                    onClick={() => props.onDelete(item)}
                  >
                    刪除
                  </button>
                  <button
                    className="primary"
                    disabled={props.pending}
                    type="button"
                    onClick={() => props.onSave(item)}
                  >
                    儲存題目
                  </button>
                </footer>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}

export function MobileActivityItemComposerView(props: ComposerViewProps) {
  return (
    <main
      aria-label="行動版題目編輯器"
      className="activity-item-composer-mobile"
      data-testid="mobile-item-editor"
    >
      <header className="mobile-composer-header">
        <div>
          <b>編輯題目</b>
          <small>{`版本 ${props.activityRevision}`}</small>
        </div>
        <nav aria-label="新增題型" className="mobile-question-type-grid">
          {(Object.keys(typeLabels) as ActivityItemDraft['itemType'][]).map((type) => (
            <button
              disabled={props.pending}
              key={type}
              type="button"
              onClick={() => props.onAdd(type)}
            >
              {`新增${typeLabels[type]}`}
            </button>
          ))}
        </nav>
      </header>
      <section aria-label="圖片匯入" className="mobile-image-import">
        <label className="mobile-image-picker">
          <span>從裝置選擇單題圖片</span>
          <input
            accept={acceptedImageTypes}
            aria-label="從裝置選擇單題圖片"
            key={props.selectedImage?.name ?? 'empty'}
            type="file"
            onChange={(event) => props.onImageSelected(event.target.files?.[0] ?? null)}
          />
        </label>
        <p>{props.selectedImage?.name ?? '支援 PNG、JPEG、WebP，最大 10 MB'}</p>
        <button
          disabled={props.pending || !props.selectedImage}
          type="button"
          onClick={props.onImport}
        >
          上傳並加入題目
        </button>
      </section>
      <AttemptMessages attempts={props.attempts} error={props.error} />
      {props.items.length === 0 ? (
        <section className="activity-item-composer-empty" role="status">
          <b>這是一份空白草稿</b>
          <p>從上方選擇題型開始。</p>
        </section>
      ) : (
        <div className="mobile-question-stack">
          {props.items.map((item, index) => {
            const draft = props.drafts[item.id] ?? draftFromItem(item);
            const prompt = String(draft.questionContent.text ?? draft.questionContent.prompt ?? '');
            return (
              <section
                className="mobile-question-card"
                data-activity-item-id={item.id}
                key={item.id}
              >
                <header className="mobile-question-card-header">
                  <b>{`第 ${index + 1} 題`}</b>
                  <span>{`v${item.revision}`}</span>
                  <select
                    aria-label={`第 ${index + 1} 題題型`}
                    value={draft.itemType}
                    onChange={(event) =>
                      props.onDraft(
                        item.id,
                        changedItemType(draft, event.target.value as ActivityItemDraft['itemType']),
                      )
                    }
                  >
                    {(Object.keys(typeLabels) as ActivityItemDraft['itemType'][]).map((type) => (
                      <option key={type} value={type}>
                        {typeLabels[type]}
                      </option>
                    ))}
                  </select>
                </header>
                <label className="mobile-question-prompt">
                  題幹
                  <textarea
                    aria-label={`第 ${index + 1} 題題幹`}
                    value={prompt}
                    onChange={(event) =>
                      props.onDraft(item.id, {
                        ...draft,
                        questionContent: { ...draft.questionContent, text: event.target.value },
                      })
                    }
                  />
                </label>
                {draft.itemType === 'choice' ? (
                  <label className="mobile-question-options">
                    選項（每行一個）
                    <textarea
                      aria-label={`第 ${index + 1} 題選項`}
                      value={draft.options.map((option) => String(option.text ?? '')).join('\n')}
                      onChange={(event) =>
                        props.onDraft(item.id, {
                          ...draft,
                          options: event.target.value.split('\n').map((text, optionIndex) => ({
                            id: String(draft.options[optionIndex]?.id ?? crypto.randomUUID()),
                            text,
                          })),
                        })
                      }
                    />
                  </label>
                ) : null}
                <div className="mobile-answer-fields">
                  <label>
                    答案
                    <input
                      aria-label={`第 ${index + 1} 題答案`}
                      value={String(draft.answerKey?.answer ?? '')}
                      onChange={(event) =>
                        props.onDraft(item.id, {
                          ...draft,
                          answerKey: { ...draft.answerKey, answer: event.target.value },
                        })
                      }
                    />
                  </label>
                  <label>
                    配分
                    <input
                      aria-label={`第 ${index + 1} 題配分`}
                      min="0.01"
                      step="0.5"
                      type="number"
                      value={draft.maxPoints}
                      onChange={(event) =>
                        props.onDraft(item.id, {
                          ...draft,
                          maxPoints: Math.max(0.01, Number(event.target.value) || 1),
                        })
                      }
                    />
                  </label>
                  <label className="activity-item-checkbox">
                    <input
                      checked={draft.requiresShowWork}
                      type="checkbox"
                      onChange={(event) =>
                        props.onDraft(item.id, { ...draft, requiresShowWork: event.target.checked })
                      }
                    />
                    需列式
                  </label>
                </div>
                <div className="mobile-order-actions">
                  <button
                    disabled={props.pending || index === 0}
                    type="button"
                    onClick={() => props.onMove(item, -1)}
                  >
                    上移
                  </button>
                  <button
                    disabled={props.pending || index === props.items.length - 1}
                    type="button"
                    onClick={() => props.onMove(item, 1)}
                  >
                    下移
                  </button>
                </div>
                <footer className="mobile-question-actions">
                  <button
                    disabled={props.pending}
                    type="button"
                    onClick={() => props.onDuplicate(item)}
                  >
                    複製
                  </button>
                  <button
                    disabled={props.pending}
                    type="button"
                    onClick={() => props.onGenerate(item)}
                  >
                    相似題
                  </button>
                  <button
                    disabled={props.pending}
                    type="button"
                    onClick={() => props.onDelete(item)}
                  >
                    刪除
                  </button>
                  <button
                    className="primary"
                    disabled={props.pending}
                    type="button"
                    onClick={() => props.onSave(item)}
                  >
                    儲存
                  </button>
                </footer>
              </section>
            );
          })}
        </div>
      )}
    </main>
  );
}

export function ActivityItemComposer({
  activityId,
  activityRevision,
  attempts,
  items,
}: {
  activityId: string;
  activityRevision: number;
  attempts: ActivityItemCreationAttempt[];
  items: ActivityEditorItem[];
}) {
  const mobile = useIsMobile();
  const notify = useNotify();
  const createItem = useCreateActivityItem();
  const updateItem = useUpdateActivityItem();
  const duplicateItem = useDuplicateActivityItem();
  const reorderItems = useReorderActivityItems();
  const deleteItem = useDeleteActivityItem();
  const generateSimilar = useGenerateSimilarActivityItem();
  const uploadImage = useUploadSingleQuestionImage();
  const importQuestion = useImportSingleQuestion();
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [selectionError, setSelectionError] = useState<string>();
  const [drafts, setDrafts] = useState<Record<string, ActivityItemDraft>>({});
  const retainedKeys = useRef<Record<string, string>>({});
  const previousItemIds = useRef(new Set(items.map((item) => item.id)));
  const retainedKey = (scope: string) => {
    retainedKeys.current[scope] ??= nextKey(scope.replaceAll(':', '-'));
    return retainedKeys.current[scope];
  };
  const complete = (scope: string, notifyMessage?: string) => () => {
    delete retainedKeys.current[scope];
    if (notifyMessage) notify(notifyMessage);
  };
  useEffect(() => {
    setDrafts(Object.fromEntries(items.map((item) => [item.id, draftFromItem(item)])));
    const added = items.find((item) => !previousItemIds.current.has(item.id));
    previousItemIds.current = new Set(items.map((item) => item.id));
    if (added) {
      window.requestAnimationFrame(() => {
        document
          .querySelector<HTMLElement>(`[data-activity-item-id="${added.id}"] textarea`)
          ?.focus();
      });
    }
  }, [items]);
  const mutations = [
    createItem,
    updateItem,
    duplicateItem,
    reorderItems,
    deleteItem,
    generateSimilar,
    uploadImage,
    importQuestion,
  ];
  const pending = mutations.some((mutation) => mutation.isPending);
  const error = mutations.find((mutation) => mutation.error)?.error;
  const errorMessage = selectionError ?? (error instanceof Error ? error.message : undefined);
  const base = () => ({ activityId, expectedActivityRevision: activityRevision });

  const viewProps: ComposerViewProps = {
    activityRevision,
    attempts,
    drafts,
    error: errorMessage,
    items,
    pending,
    selectedImage,
    onAdd: (itemType) => {
      const scope = `create:${itemType}`;
      createItem.mutate(
        {
          ...base(),
          idempotencyKey: retainedKey(scope),
          item: blankDraft(itemType),
          afterItemId: items.at(-1)?.id,
        },
        { onSuccess: complete(scope) },
      );
    },
    onDelete: (item) => {
      const scope = `delete:${item.id}`;
      deleteItem.mutate(
        {
          ...base(),
          itemId: item.id,
          expectedItemRevision: item.revision,
          idempotencyKey: retainedKey(scope),
        },
        { onSuccess: complete(scope) },
      );
    },
    onDraft: (itemId, draft) => setDrafts((current) => ({ ...current, [itemId]: draft })),
    onDuplicate: (item) => {
      const scope = `duplicate:${item.id}`;
      duplicateItem.mutate(
        {
          ...base(),
          itemId: item.id,
          expectedItemRevision: item.revision,
          idempotencyKey: retainedKey(scope),
        },
        { onSuccess: complete(scope) },
      );
    },
    onGenerate: (item) => {
      const scope = `similar:${item.id}`;
      generateSimilar.mutate(
        {
          ...base(),
          itemId: item.id,
          expectedItemRevision: item.revision,
          afterItemId: item.id,
          idempotencyKey: retainedKey(scope),
        },
        { onSuccess: complete(scope, '已排入相似題產生佇列') },
      );
    },
    onImageSelected: (file) => {
      if (
        file &&
        (!acceptedImageTypes.split(',').includes(file.type) || file.size > maximumImageBytes)
      ) {
        setSelectedImage(null);
        setSelectionError('請選擇 10 MB 以內的 PNG、JPEG 或 WebP 圖片。');
        return;
      }
      setSelectionError(undefined);
      setSelectedImage(file);
    },
    onImport: () => {
      if (!selectedImage) return;
      uploadImage.mutate(
        { activityId, file: selectedImage },
        {
          onSuccess: (uploaded) => {
            const scope = `image-import:${uploaded.assetId}`;
            importQuestion.mutate(
              {
                ...base(),
                assetId: uploaded.assetId,
                afterItemId: items.at(-1)?.id,
                idempotencyKey: retainedKey(scope),
              },
              {
                onSuccess: () => {
                  complete(scope, '已排入單題圖片辨識佇列')();
                  setSelectedImage(null);
                },
              },
            );
          },
        },
      );
    },
    onMove: (item, direction) => {
      const index = items.findIndex((candidate) => candidate.id === item.id);
      const target = index + direction;
      if (target < 0 || target >= items.length) return;
      const orderedItemIds = items.map((candidate) => candidate.id);
      [orderedItemIds[index], orderedItemIds[target]] = [
        orderedItemIds[target] ?? item.id,
        orderedItemIds[index] ?? item.id,
      ];
      const scope = `reorder:${orderedItemIds.join(':')}`;
      reorderItems.mutate(
        { ...base(), orderedItemIds, idempotencyKey: retainedKey(scope) },
        { onSuccess: complete(scope) },
      );
    },
    onReorder: (sourceItemId, targetItemId) => {
      const orderedItemIds = items.map((item) => item.id);
      const sourceIndex = orderedItemIds.indexOf(sourceItemId);
      const targetIndex = orderedItemIds.indexOf(targetItemId);
      if (sourceIndex < 0 || targetIndex < 0) return;
      const [moved] = orderedItemIds.splice(sourceIndex, 1);
      if (!moved) return;
      orderedItemIds.splice(targetIndex, 0, moved);
      const scope = `reorder:${orderedItemIds.join(':')}`;
      reorderItems.mutate(
        { ...base(), orderedItemIds, idempotencyKey: retainedKey(scope) },
        { onSuccess: complete(scope) },
      );
    },
    onSave: (item) => {
      const scope = `update:${item.id}`;
      updateItem.mutate(
        {
          ...base(),
          itemId: item.id,
          expectedItemRevision: item.revision,
          idempotencyKey: retainedKey(scope),
          item: drafts[item.id] ?? draftFromItem(item),
        },
        { onSuccess: complete(scope) },
      );
    },
  };

  return mobile ? (
    <MobileActivityItemComposerView {...viewProps} />
  ) : (
    <DesktopActivityItemComposerView {...viewProps} />
  );
}
